import React, { useContext, useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    Paper,
    Divider,
    CircularProgress,
} from "@mui/material";
import axios from "axios";
import { QueryParameterNames } from "../../components/api-authorization/ApiAuthorizationConstants";
import { Nominee } from "../../constants/roles";
import { UserContext, UserContextType } from "./UserContext";

interface IUser {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    extension: string;
    isEpiqUser: boolean;
    countryCode: string;
}

interface IInstitution {
    name: string;
    address1: string;
    address2?: string;
    address3?: string;
    city: string;
    stateProvince: string;
    zipCode: string;
    country: string;
}

interface IUserProfile {
    user: IUser;
    institution: IInstitution;
}

interface ProfileData {
    isEpiqUser: boolean;
    userEmail: string;
    firstName: string;
    lastName: string;
    phone: string;
    extension: string;
    institutionName: string;
    institutionAddress1: string;
    institutionAddress2?: string;
    institutionAddress3?: string;
    institutionCity: string;
    institutionState: string;
    institutionZipcode: string;
    institutionCountry: string;
    countryCode: string;
}

interface FormValues {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    phoneExtension: string;
}

interface FormErrors {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
}

const ProfileDialog = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [profileData, setProfileData] = useState<ProfileData | null>(null);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [formValues, setFormValues] = useState<FormValues>({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        phoneExtension: "",
    });
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const { isInRole } = useContext(UserContext) as UserContextType;

    const fetchProfileData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get<IUserProfile>("/api/user/profile");


            setProfileData({
                isEpiqUser: response.data.user.isEpiqUser,
                userEmail: response.data.user.email,
                firstName: response.data.user.firstName,
                lastName: response.data.user.lastName,
                phone: response.data.user.phone,
                extension: response.data.user.extension,
                countryCode: response.data.user.countryCode,
                institutionName: response.data.institution.name,
                institutionAddress1: response.data.institution.address1,
                institutionAddress2: response.data.institution.address2,
                institutionAddress3: response.data.institution.address3,
                institutionCity: response.data.institution.city,
                institutionState: response.data.institution.stateProvince,
                institutionZipcode: response.data.institution.zipCode,
                institutionCountry: response.data.institution.country,
            });

            setFormValues({
                firstName: response.data.user.firstName,
                lastName: response.data.user.lastName,
                phoneNumber: response.data.user.phone,
                phoneExtension: response.data.user.extension,
            });
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "An error occurred while fetching profile data"
            );
        } finally {
            setLoading(false);
        }
    };

    const [returnUrl, setReturnUrl] = useState<string | null>(null);

    useEffect(() => {
        fetchProfileData();

        let params = new URLSearchParams(window.location.search);
        let fromQuery = params.get(QueryParameterNames.ReturnUrl);
        setReturnUrl(fromQuery);
    }, []);

    const validateForm = (): boolean => {
        const errors: FormErrors = {};
        let isValid = true;

        if (!formValues.firstName.trim()) {
            errors.firstName = "First Name is required";
            isValid = false;
        }

        if (!formValues.lastName.trim()) {
            errors.lastName = "Last Name is required";
            isValid = false;
        }

        if (!formValues.phoneNumber.trim()) {
            errors.phoneNumber = "Phone Number is required";
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            let updatedUser: IUser = {
                email: profileData!.userEmail,
                firstName: formValues.firstName,
                lastName: formValues.lastName,
                phone: formValues.phoneNumber,
                extension: formValues.phoneExtension,
                isEpiqUser: profileData!.isEpiqUser,
                countryCode: profileData!.countryCode
            }

            await axios.post("/api/user/profile", updatedUser);
            setStatusMessage("Profile updated successfully!");
            setTimeout(() => {
                setStatusMessage(null);
            }, 3000);
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "An error occurred while updating profile"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name } = e.target;
        setTouched((prev) => ({
            ...prev,
            [name]: true,
        }));
        validateForm();
    };

    const closeAndExit = () => {
        if (returnUrl) {
            window.location.href = returnUrl;
        } else {
            window.location.href = "/";
        }
    }

    const onClose = (e: any, reason: string) => {
        e.preventDefault();

        if (reason === 'backdropClick') {
            return;
        }
    }

    const [isOpen, setIsOpen] = useState(true);

    if (loading && !profileData) {
        return (
            <Dialog open={isOpen} onClose={onClose}>
                <DialogContent>
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="200px"
                    >
                        <CircularProgress />
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    const handleClose = () => {
        closeAndExit();
    }

    if (error) {
        return (
            <Dialog open={true} onClose={onClose}>
                <DialogContent>
                    <Alert severity="error">{error}</Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                    <Button onClick={fetchProfileData} color="primary">
                        Retry
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    const handleCancel = () => {
        closeAndExit();
    }

    const handlePassword = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setIsOpen(false);

        if (returnUrl)
            window.location.href = `/changeCredentials?${QueryParameterNames.ReturnUrl}=${returnUrl}`;
        else
            window.location.href = `/changeCredentials`;
    }

    return (
        <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontSize: '2rem' }}>Profile</DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    {statusMessage && (
                        <Alert
                            severity="success"
                            sx={{ mt: 2, mb: 2 }}
                            style={{ backgroundColor: "green", color: "white" }}
                        >
                            {statusMessage}
                        </Alert>
                    )}

                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            id="firstName"
                            name="firstName"
                            label="First Name"
                            value={formValues.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.firstName && Boolean(formErrors.firstName)}
                            helperText={touched.firstName && formErrors.firstName}
                            sx={{ mb: 2 }}
                            variant="standard"
                            required
                        />

                        <TextField
                            fullWidth
                            id="lastName"
                            name="lastName"
                            label="Last Name"
                            value={formValues.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.lastName && Boolean(formErrors.lastName)}
                            helperText={touched.lastName && formErrors.lastName}
                            sx={{ mb: 2 }}
                            variant="standard"
                            required
                        />

                        <TextField
                            fullWidth
                            id="phoneNumber"
                            name="phoneNumber"
                            label="Phone Number"
                            value={formValues.phoneNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.phoneNumber && Boolean(formErrors.phoneNumber)}
                            helperText={touched.phoneNumber && formErrors.phoneNumber}
                            sx={{ mb: 2 }}
                            variant="standard"
                        />

                        <TextField
                            fullWidth
                            id="phoneExtension"
                            name="phoneExtension"
                            label="Phone Extension"
                            value={formValues.phoneExtension}
                            onChange={handleChange}
                            sx={{ mb: 2 }}
                            variant="standard"
                        />
                    </Box>

                    {profileData && (
                        <Paper sx={{ p: 2, bgcolor: "#eeeeee", mt: 2, mb: 2 }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Username
                                </Typography>
                                <Typography>{profileData.userEmail}</Typography>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Institution Information
                                </Typography>
                                <Typography>{profileData.institutionName}</Typography>
                                <Typography>{profileData.institutionAddress1}</Typography>
                                {profileData.institutionAddress2 && (
                                    <Typography>{profileData.institutionAddress2}</Typography>
                                )}
                                {profileData.institutionAddress3 && (
                                    <Typography>{profileData.institutionAddress3}</Typography>
                                )}
                                <Typography>
                                    {profileData.institutionCity}, {profileData.institutionState}{" "}
                                    {profileData.institutionZipcode}
                                </Typography>
                                <Typography>
                                    {profileData.institutionCountry}
                                </Typography>
                            </Box>
                            {!isInRole([Nominee]) &&
                                <>
                                    <Divider sx={{ my: 2 }} />

                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Country Code
                                        </Typography>
                                        <Typography>
                                            {profileData.countryCode}
                                        </Typography>
                                    </Box>
                                </>}
                        </Paper>
                    )}
                </form>
            </DialogContent>

            <DialogActions sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 3, // Add horizontal padding of 24px (theme spacing 3)
                pb: 2  // Add bottom padding of 16px (theme spacing 2)
            }}>
                {profileData?.isEpiqUser ?
                    <Box>
                    </Box> :
                    <Box>
                        <Button variant="outlined" onClick={handlePassword}>Password</Button>
                    </Box>
                }
                <Box>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : "Save"}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default ProfileDialog;

